// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Rewards Page
// Loyalty program, bonuses, daily rewards
// ================================================================
import React, { useState } from 'react';
import { useCasino } from '../context/CasinoShell';

const DAILY_REWARDS = [
  { day: 1, reward: 5, claimed: true },
  { day: 2, reward: 10, claimed: true },
  { day: 3, reward: 15, claimed: false },
  { day: 4, reward: 25, claimed: false },
  { day: 5, reward: 50, claimed: false },
  { day: 6, reward: 75, claimed: false },
  { day: 7, reward: 150, claimed: false },
];

const LOYALTY_TIERS = [
  { name: 'BRONZE', min: 0, max: 500, color: '#CD7F32', perks: ['5% deposit bonus', 'Daily free spin'] },
  { name: 'SILVER', min: 500, max: 2000, color: '#C0C0C0', perks: ['10% deposit bonus', '3 daily free spins', 'Priority support'] },
  { name: 'GOLD', min: 2000, max: 10000, color: '#FFD700', perks: ['15% deposit bonus', '5 daily free spins', 'Exclusive games', 'Weekly cashback'] },
  { name: 'PLATINUM', min: 10000, max: Infinity, color: '#E5E4E2', perks: ['25% deposit bonus', '10 daily free spins', 'VIP manager', 'Monthly bonus', 'Exclusive tournaments'] },
];

export default function RewardsPage() {
  const { sessionStats, deposit } = useCasino();
  const [claimed, setClaimed] = useState(false);

  const totalWagered = sessionStats.totalBets;
  const currentTier = LOYALTY_TIERS.find(t => totalWagered >= t.min && totalWagered < t.max) || LOYALTY_TIERS[0];
  const nextTier = LOYALTY_TIERS[LOYALTY_TIERS.indexOf(currentTier) + 1];
  const progress = nextTier ? ((totalWagered - currentTier.min) / (nextTier.min - currentTier.min)) * 100 : 100;

  const handleClaimDaily = () => {
    const reward = DAILY_REWARDS.find(d => !d.claimed);
    if (reward) {
      deposit(reward.reward);
      setClaimed(true);
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🎁 REWARDS</h1>

      {/* Loyalty Tier */}
      <div style={{ ...styles.tierCard, borderColor: currentTier.color + '66' }}>
        <div style={styles.tierHeader}>
          <span style={{ ...styles.tierName, color: currentTier.color }}>{currentTier.name}</span>
          {nextTier && <span style={styles.nextTier}>Next: {nextTier.name}</span>}
        </div>
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${Math.min(progress, 100)}%`, background: currentTier.color }} />
        </div>
        <p style={styles.progressText}>
          ${totalWagered.toFixed(0)} wagered{nextTier ? ` / $${nextTier.min} for ${nextTier.name}` : ' — MAX TIER'}
        </p>
        <div style={styles.perks}>
          {currentTier.perks.map((perk, i) => (
            <span key={i} style={styles.perk}>✅ {perk}</span>
          ))}
        </div>
      </div>

      {/* Daily Rewards */}
      <div style={styles.dailyCard}>
        <h3 style={styles.sectionTitle}>DAILY LOGIN REWARDS</h3>
        <div style={styles.dailyGrid}>
          {DAILY_REWARDS.map(day => (
            <div key={day.day} style={{
              ...styles.dayCard,
              background: day.claimed ? '#00FF8822' : '#111',
              borderColor: day.claimed ? '#00FF8866' : '#222',
            }}>
              <span style={styles.dayNum}>Day {day.day}</span>
              <span style={styles.dayReward}>${day.reward}</span>
              {day.claimed && <span style={styles.dayCheck}>✅</span>}
            </div>
          ))}
        </div>
        <button
          style={{ ...styles.claimBtn, opacity: claimed ? 0.5 : 1 }}
          onClick={handleClaimDaily}
          disabled={claimed}
        >
          {claimed ? 'CLAIMED TODAY' : 'CLAIM DAILY REWARD'}
        </button>
      </div>

      {/* Promotions */}
      <div style={styles.promoCard}>
        <h3 style={styles.sectionTitle}>ACTIVE PROMOTIONS</h3>
        <div style={styles.promo}>
          <span style={styles.promoIcon}>🔥</span>
          <div>
            <h4 style={styles.promoTitle}>FIRST DEPOSIT BONUS</h4>
            <p style={styles.promoText}>100% match on your first deposit up to $500 $FORGE</p>
          </div>
        </div>
        <div style={styles.promo}>
          <span style={styles.promoIcon}>⚡</span>
          <div>
            <h4 style={styles.promoTitle}>WEEKEND RUSH</h4>
            <p style={styles.promoText}>Double loyalty points every Saturday and Sunday</p>
          </div>
        </div>
        <div style={styles.promo}>
          <span style={styles.promoIcon}>🏆</span>
          <div>
            <h4 style={styles.promoTitle}>WEEKLY TOURNAMENT</h4>
            <p style={styles.promoText}>Top 10 winners split $1,000 $FORGE prize pool</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 600, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, marginBottom: 16 },
  tierCard: { padding: 16, background: '#111', borderRadius: 12, border: '2px solid', marginBottom: 16 },
  tierHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  tierName: { fontSize: 18, fontWeight: 900, letterSpacing: 3 },
  nextTier: { fontSize: 10, color: '#666' },
  progressBar: { width: '100%', height: 8, background: '#222', borderRadius: 4, overflow: 'hidden', marginBottom: 6 },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  progressText: { fontSize: 10, color: '#888', marginBottom: 10 },
  perks: { display: 'flex', flexDirection: 'column', gap: 4 },
  perk: { fontSize: 11, color: '#ccc' },
  dailyCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 16 },
  sectionTitle: { fontSize: 12, color: '#DC2626', fontWeight: 900, letterSpacing: 2, marginBottom: 12, marginTop: 0 },
  dailyGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 12 },
  dayCard: {
    padding: '8px 4px', borderRadius: 6, border: '1px solid', textAlign: 'center',
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  dayNum: { fontSize: 7, color: '#666', fontWeight: 700 },
  dayReward: { fontSize: 11, color: '#FFD700', fontWeight: 900 },
  dayCheck: { fontSize: 10 },
  claimBtn: {
    width: '100%', padding: 10, borderRadius: 8, border: '2px solid #FFD700',
    background: '#FFD70033', color: '#FFD700', fontSize: 12, fontWeight: 900,
    letterSpacing: 2, cursor: 'pointer',
  },
  promoCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222' },
  promo: { display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #1a1a1a', alignItems: 'flex-start' },
  promoIcon: { fontSize: 24 },
  promoTitle: { fontSize: 11, color: '#DC2626', fontWeight: 900, letterSpacing: 1, margin: '0 0 4px' },
  promoText: { fontSize: 11, color: '#888', margin: 0, lineHeight: 1.4 },
};
